import React, { useState } from 'react';
import { Form, Input, Select, Modal, Switch, Row, Col, notification } from 'antd';
import { SubmitButton, SubmitButtonCol } from '../styledComponents';
import DeactivateBillerGroupModal from './DeactivateBillerGroupModal';
import { getBillerGroupAuthors, getBillerAssignmentPicker } from '../../../Apis/Biller';

const { Option } = Select;

const AddBillerGroupForm = ({ billers, selectedItem, submitHandler, submitLoading, onStatusChange }) => {
  const [form] = Form.useForm();
  const [active, setActive] = useState(selectedItem?.active ?? true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [affectedAuthors, setAffectedAuthors] = useState([]);
  const [reassignOptions, setReassignOptions] = useState({ billers: [], billerGroups: [] });
  // Bumped on every open so the modal remounts with an empty selection; a
  // target picked before Cancel may no longer be valid on the next open.
  const [deactivateModalKey, setDeactivateModalKey] = useState(0);

  const initialGroupName = selectedItem?.groupName || '';
  const initialBillerIds = (selectedItem?.billers?.map(b => b.id) || []).slice().sort().join(',');

  const onValuesChange = () => {
    if (!selectedItem) return;
    const current = form.getFieldsValue();
    const nameChanged = (current.groupName || '') !== initialGroupName;
    const currentBillerIds = (current.billerIds || []).slice().sort().join(',');
    setHasChanges(nameChanged || currentBillerIds !== initialBillerIds);
  };

  const authorsLoadFailed = message => {
    setStatusLoading(false);
    notification['error']({
      message: 'Failed',
      description: message || 'Unable to load the doctors and admins assigned to this group.'
    });
  };

  // TRPA-8544: list the doctors/admins assigned to this group and let the admin
  // optionally reassign them in the same call that deactivates the group.
  // The reassign targets come from the assignment picker; if it fails the
  // modal still opens and the admin can deactivate without reassigning.
  const openDeactivateModal = () => {
    setStatusLoading(true);
    Promise.all([
      getBillerGroupAuthors(selectedItem.id),
      getBillerAssignmentPicker().catch(() => null)
    ])
      .then(([authorsResponse, pickerResponse]) => {
        if (!authorsResponse.data.success) {
          authorsLoadFailed(authorsResponse.data.message);
          return;
        }
        const picker = pickerResponse?.data?.success ? pickerResponse.data.result[0] || {} : {};
        setAffectedAuthors(authorsResponse.data.result || []);
        setReassignOptions({
          billers: picker.billers || [],
          billerGroups: picker.billerGroups || []
        });
        setStatusLoading(false);
        setDeactivateModalKey(k => k + 1);
        setDeactivateVisible(true);
      })
      .catch(() => authorsLoadFailed());
  };

  const confirmDeactivate = payload => {
    setDeactivateSubmitting(true);
    onStatusChange(
      selectedItem.id,
      payload,
      () => {
        setActive(false);
        setDeactivateSubmitting(false);
        setDeactivateVisible(false);
      },
      () => setDeactivateSubmitting(false)
    );
  };

  const handleToggle = checked => {
    if (!checked) {
      openDeactivateModal();
    } else {
      setStatusLoading(true);
      onStatusChange(selectedItem.id, { active: true }, () => {
        setActive(true);
        setStatusLoading(false);
      }, () => setStatusLoading(false));
    }
  };

  const onFinish = values => {
    if (selectedItem && (!values.billerIds || values.billerIds.length === 0)) {
      Modal.confirm({
        title: 'Group will have no members',
        content:
          'Removing all billers will leave this group with zero members. Billers will stop receiving new jobs from this queue. Do you want to proceed?',
        onOk: () => submitHandler(values),
        okText: 'Confirm',
        cancelText: 'Cancel',
        okButtonProps: { danger: true }
      });
      return;
    }
    submitHandler(values);
  };

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
      onValuesChange={onValuesChange}
      initialValues={{
        ...selectedItem,
        billerIds: selectedItem?.billers?.map(b => b.id) || []
      }}
    >
      <Form.Item
        label='Group Name'
        name='groupName'
        rules={[{ required: true, message: 'Please enter a group name' }]}
      >
        <Input placeholder='Enter group name' size='large' />
      </Form.Item>

      <Form.Item
        label='Billers'
        name='billerIds'
        rules={[
          {
            validator: (_, value) => {
              if (!selectedItem && (!value || value.length === 0)) {
                return Promise.reject(new Error('Please select at least one biller'));
              }
              return Promise.resolve();
            }
          }
        ]}
      >
        <Select
          size='large'
          mode='multiple'
          placeholder='Select billers'
          optionFilterProp='children'
          showSearch
        >
          {(billers || []).map(b => (
            <Option key={b.id} value={b.id}>
              {b.fullName}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {selectedItem && (
        <Form.Item label='Status'>
          <Row align='middle' gutter={8}>
            <Col>
              <Switch
                checked={active}
                onChange={handleToggle}
                loading={statusLoading}
              />
            </Col>
            <Col>
              <span style={{ color: active ? '#52c41a' : '#aaa' }}>
                {active ? 'Active' : 'Inactive'}
              </span>
            </Col>
          </Row>
        </Form.Item>
      )}

      <SubmitButtonCol span={24}>
        <SubmitButton
          htmlType='submit'
          type='primary'
          size='large'
          shape='round'
          loading={submitLoading}
          disabled={selectedItem ? !hasChanges : false}
        >
          Submit
        </SubmitButton>
      </SubmitButtonCol>
      </Form>
      {selectedItem && (
        <DeactivateBillerGroupModal
          key={deactivateModalKey}
          visible={deactivateVisible}
          groupId={selectedItem.id}
          authors={affectedAuthors}
          billers={reassignOptions.billers}
          billerGroups={reassignOptions.billerGroups}
          submitting={deactivateSubmitting}
          onCancel={() => setDeactivateVisible(false)}
          onConfirm={confirmDeactivate}
        />
      )}
    </>
  );
};

export default AddBillerGroupForm;
